import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, FileText, Check, X } from 'lucide-react'
import { solarAPI } from '../../services/api'
import { SolarPanel } from '../../types'
import { Alert } from '../common/Alert'
import { Button } from '../common/Button'

export const UploadPDF: React.FC = () => {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [parsedPanels, setParsedPanels] = useState<SolarPanel[]>([])
  const [duplicates, setDuplicates] = useState<SolarPanel[]>([])
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setError('')
    } else {
      setError('Please select a valid PDF file')
    }
  }

  const handleUpload = async (force: boolean = false) => {
    if (!file) return
    setLoading(true)
    setError('')
    setSuccess('')
    try {
      const response = await solarAPI.uploadPDF(file, force)
      const { duplicates: dup = [], panels: pnl = [] } = response
      setParsedPanels(pnl)
      if (dup.length > 0 && !force) {
        setDuplicates(dup)
        setShowDuplicateAlert(true)
      } else {
        setDuplicates([])
        setShowDuplicateAlert(false)
        setSuccess(`Successfully parsed ${pnl.length} panels from PDF`)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload PDF')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePanel = async (panel: SolarPanel, index: number) => {
    try {
      await solarAPI.create(panel)
      setParsedPanels(prev => prev.filter((_, i) => i !== index))
      setSuccess('Panel created successfully!')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create panel')
    }
  }

  const resetUpload = () => {
    setFile(null)
    setParsedPanels([])
    setDuplicates([])
    setShowDuplicateAlert(false)
    setError('')
    setSuccess('')
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Upload PDF to Extract Panel Data</h1>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <AnimatePresence>
        {showDuplicateAlert && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
            <Alert
              type="warning"
              title="Duplicate Panels Found"
              message={`Found ${duplicates.length} duplicate panels. They’re shown below.`}
            />
            <div className="flex space-x-4 mb-6">
              <Button onClick={() => handleUpload(true)} variant="primary">
                Continue with All
              </Button>
              <Button onClick={() => setShowDuplicateAlert(false)} variant="secondary">
                Cancel
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          {!file ? (
            <>
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg text-gray-600 mb-4">Drop your PDF file here or click to browse</p>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
              <Button variant="primary" className="cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                Select PDF File
              </Button>
            </>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <FileText className="h-12 w-12 text-[#63B56F] mx-auto" />
              <p className="text-lg font-medium text-gray-900">{file.name}</p>
              <p className="text-sm text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              <div className="flex justify-center space-x-4">
                <Button onClick={() => handleUpload(false)} loading={loading} variant="primary">
                  Process PDF
                </Button>
                <Button onClick={resetUpload} variant="secondary" disabled={loading}>
                  Remove File
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {(duplicates.length > 0 || parsedPanels.length > 0) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <div className="flex flex-col md:flex-row md:space-x-8">
              {duplicates.length > 0 && (
                <div className="md:w-1/2 mb-8 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Duplicate Panels ({duplicates.length})</h2>
                  <div className="space-y-4">
                    {duplicates.map((panel, index) => (
                      <motion.div
                        key={`dup-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-yellow-50 rounded-lg shadow-md p-6"
                      >
                        <h3 className="text-lg font-semibold text-gray-900">{panel.SKU}</h3>
                        <p className="text-gray-600">{panel.Description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4">
                          <div><span className="font-medium">Power:</span> {panel.Watts_W}W</div>
                          <div><span className="font-medium">Efficiency:</span> {panel.ModuleEfficiency_percent}%</div>
                          <div><span className="font-medium">Dimensions:</span> {panel.Length_mm}×{panel.Width_mm}mm</div>
                          <div><span className="font-medium">Weight:</span> {panel.Weight_kg}kg</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              {parsedPanels.length > 0 && (
                <div className="md:w-1/2">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Parsed Panels ({parsedPanels.length})</h2>
                  <div className="space-y-4">
                    {parsedPanels.map((panel, index) => (
                      <motion.div
                        key={`parsed-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-lg shadow-md p-6"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{panel.SKU}</h3>
                            <p className="text-gray-600">{panel.Description}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button onClick={() => handleCreatePanel(panel, index)} variant="primary" size="sm">
                              <Check className="h-4 w-4 mr-1" />
                              Insert
                            </Button>
                            <Button onClick={() => setParsedPanels(prev => prev.filter((_, i) => i !== index))} variant="danger" size="sm">
                              <X className="h-4 w-4 mr-1" />
                              Skip
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div><span className="font-medium">Power:</span> {panel.Watts_W}W</div>
                          <div><span className="font-medium">Efficiency:</span> {panel.ModuleEfficiency_percent}%</div>
                          <div><span className="font-medium">Dimensions:</span> {panel.Length_mm}×{panel.Width_mm}mm</div>
                          <div><span className="font-medium">Weight:</span> {panel.Weight_kg}kg</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
